// @ts-nocheck
import { MOCK } from "@/state/app-store";
import { UI } from "@/components/ui";
import { Icon } from "@/components/icons";
import { api } from "@/api/index";

const { $, $$, fmtNum, fmtMoney, fmtPercent, toast, confirmModal, hasPerm, statusBadge, trendArrow, statCard, channelChip, capabilityBadge, pageHeader, emptyState, primaryBtn, ghostBtn, stepIndicator, BUSINESS_COLORS, chartTooltip, chartXAxis, chartYAxis, chartLineSeries, chartBarSeries } = UI;

declare const echarts: any;
declare const Pages: Record<string, any>;
declare const PageHooks: { mounted?: ((path: string) => void) | null };
declare function navigate(path: string): void;
declare function getCurrentPath(): string;
declare function getCurrentPathWithQuery(): string;
declare function renderRoute(): void;

if (typeof window !== "undefined") {
  (window as any).Pages = (window as any).Pages || {};
}

const Pages = (window as any).Pages;

// ============================================================
// 管理员功能 · 平台健康 / 平台观测
// ============================================================

window.Pages = window.Pages || {};

Pages.globalStats = function () {
  if (!UI.hasPerm("admin")) {
    return UI.emptyState({ icon: "lock", title: "权限不足", desc: "平台健康仅 SDK 管理员可访问" });
  }

  const healthItems = [
    { label: "SDK 调用成功率", value: "99.82%", status: "正常", color: "green", desc: "share / fetchConfig / trackEvent 核心接口" },
    { label: "配置发布延迟", value: "2.4 分钟", status: "正常", color: "green", desc: "发布到全球 SDK 缓存的 P95 延迟" },
    { label: "归因事件延迟", value: "38 秒", status: "观察", color: "amber", desc: "安装、回流、LTV 相关事件入库延迟" },
    { label: "渠道异常", value: "1 项", status: "需跟进", color: "rose", desc: "Facebook 爬虫缓存命中率异常" },
  ];
  const incidents = [
    { time: "2026-06-16 11:20", level: "warning", area: "渠道能力", issue: "Facebook OG 缓存刷新失败率升高", owner: "渠道 Adapter", status: "处理中" },
    { time: "2026-06-15 22:10", level: "info", area: "配置发布", issue: "活动配置发布 P95 延迟短时升高", owner: "配置平台", status: "已恢复" },
    { time: "2026-06-15 18:40", level: "info", area: "数据链路", issue: "LTV 归因日志入库延迟超过 1 分钟", owner: "数据平台", status: "已恢复" },
  ];
  const auditSummary = [
    { name: "高风险操作", value: 3, desc: "Secret 重置 / 归档 / 所有权转移" },
    { name: "权限变更", value: 7, desc: "新增成员、游戏授权、角色变更" },
    { name: "密钥操作", value: 4, desc: "OpenAPI Key 创建、重置、禁用" },
  ];

  return `
    ${UI.pageHeader({
      title: "平台健康",
      desc: "管理员视角的平台观测：关注 SDK 服务、配置发布、归因链路、渠道能力和高风险操作，不承载运营经营数据分析。",
      actions: `<button onclick="navigate('/admin/security/audit-logs')" class="px-3 py-1.5 text-sm border border-ink-200 rounded-md hover:bg-ink-50">查看审计日志</button>`,
    })}

    <div class="grid grid-cols-4 gap-4 mb-5">
      ${healthItems.map((item) => `
        <div class="card p-5 relative">
          <div class="absolute left-0 top-3 bottom-3 w-0.5 ${item.color === "green" ? "bg-ok-600" : item.color === "amber" ? "bg-warn-600" : "bg-danger-600"} rounded-r"></div>
          <div class="flex items-start justify-between gap-3">
            <div>
              <div class="text-xs text-ink-500 font-medium">${item.label}</div>
              <div class="num text-[24px] font-semibold text-ink-900 mt-1">${item.value}</div>
              <div class="text-xs text-ink-400 mt-1.5 leading-relaxed">${item.desc}</div>
            </div>
            <span class="badge ${item.color === "green" ? "bg-ok-50 text-ok-700" : item.color === "amber" ? "bg-amber-50 text-amber-700" : "bg-danger-50 text-danger-700"}">${item.status}</span>
          </div>
        </div>
      `).join("")}
    </div>

    <div class="grid grid-cols-12 gap-5 mb-5">
      <div class="col-span-8 card overflow-hidden">
        <div class="px-5 py-4 border-b border-ink-100">
          <div class="text-sm font-semibold text-ink-900">平台异常与健康事件</div>
          <div class="text-xs text-ink-500 mt-1">用于管理员判断是否需要介入渠道、配置、数据或 SDK 平台问题。</div>
        </div>
        <table class="w-full text-sm">
          <thead class="bg-ink-50 text-xs text-ink-500">
            <tr>
              <th class="text-left px-5 py-2.5 font-medium">时间</th>
              <th class="text-left px-5 py-2.5 font-medium">级别</th>
              <th class="text-left px-5 py-2.5 font-medium">模块</th>
              <th class="text-left px-5 py-2.5 font-medium">事件</th>
              <th class="text-left px-5 py-2.5 font-medium">Owner</th>
              <th class="text-left px-5 py-2.5 font-medium">状态</th>
            </tr>
          </thead>
          <tbody>
            ${incidents.map((item) => `
              <tr class="border-t border-ink-100 align-top">
                <td class="px-5 py-3 text-xs text-ink-500 font-mono">${item.time}</td>
                <td class="px-5 py-3"><span class="badge ${item.level === "warning" ? "bg-amber-50 text-amber-700" : "bg-brand-50 text-brand-700"}">${item.level}</span></td>
                <td class="px-5 py-3 text-ink-700">${item.area}</td>
                <td class="px-5 py-3 text-ink-600">${item.issue}</td>
                <td class="px-5 py-3 text-xs text-ink-500">${item.owner}</td>
                <td class="px-5 py-3"><span class="badge ${item.status === "处理中" ? "bg-amber-50 text-amber-700" : "bg-ok-50 text-ok-700"}">${item.status}</span></td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>

      <div class="col-span-4 space-y-5">
        <div class="card p-5">
          <div class="text-sm font-semibold text-ink-900">安全与审计概览</div>
          <div class="mt-3 space-y-3">
            ${auditSummary.map((item) => `
              <div class="rounded-lg border border-ink-100 p-3">
                <div class="flex items-center justify-between gap-3">
                  <div class="text-sm font-medium text-ink-900">${item.name}</div>
                  <div class="num text-lg font-semibold text-ink-900">${item.value}</div>
                </div>
                <div class="text-xs text-ink-500 mt-1">${item.desc}</div>
              </div>
            `).join("")}
          </div>
        </div>

        <div class="card p-5 bg-ink-50">
          <div class="text-sm font-semibold text-ink-900">不在本页展示的内容</div>
          <div class="text-xs text-ink-500 leading-relaxed mt-2">收入、ROI、Top 游戏、区域市场表现等经营分析已归入运营数据分析，不再作为管理员功能核心内容。</div>
        </div>
      </div>
    </div>

    <div class="card overflow-hidden">
      <div class="px-5 py-4 border-b border-ink-100">
        <div class="text-sm font-semibold text-ink-900">平台能力健康矩阵</div>
      </div>
      <table class="w-full text-sm">
        <thead class="bg-ink-50 text-xs text-ink-500">
          <tr><th class="text-left px-5 py-2.5 font-medium">能力</th><th class="text-left px-5 py-2.5 font-medium">当前状态</th><th class="text-left px-5 py-2.5 font-medium">观察指标</th><th class="text-left px-5 py-2.5 font-medium">管理员动作</th></tr>
        </thead>
        <tbody>
          ${[
            ["SDK 配置读取", "正常", "fetchConfig 成功率、P95 延迟", "观察"],
            ["分享归因链路", "正常", "Referrer 读取率、回调成功率", "观察"],
            ["渠道 Adapter", "需跟进", "渠道能力异常、OG 缓存刷新", "查看渠道能力中心"],
            ["OpenAPI Key", "正常", "最后使用、异常调用、禁用记录", "查看凭证与密钥"],
          ].map(([name, status, metric, action]) => `<tr class="border-t border-ink-100"><td class="px-5 py-3 font-medium text-ink-900">${name}</td><td class="px-5 py-3"><span class="badge ${status === "需跟进" ? "bg-amber-50 text-amber-700" : "bg-ok-50 text-ok-700"}">${status}</span></td><td class="px-5 py-3 text-ink-600">${metric}</td><td class="px-5 py-3 text-ink-600">${action}</td></tr>`).join("")}
        </tbody>
      </table>
    </div>
  `;
};

// __window_exports__
// Expose helpers for cross-page / inline onclick compatibility
